import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable';
import { DraggableTab } from './DraggableTab';
import type { TabItem, DraggableTabsProps } from './types';
import styles from './DraggableTabs.module.scss';

const EMPTY_SET = new Set<string>();

export function DraggableTabs<TTab extends TabItem = TabItem>({
  tabs,
  activeTabId,
  onTabClick,
  onReorder,
  draggable = true,
  removable = false,
  removedTabIds = EMPTY_SET,
  onRemove,
  onUndoRemove,
  className,
  ...rest
}: DraggableTabsProps<TTab>) {
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = tabs.findIndex((t) => t.id === active.id);
    const newIndex = tabs.findIndex((t) => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(arrayMove(tabs, oldIndex, newIndex));
  };

  const handleDragCancel = () => {
    setActiveDragId(null);
  };

  const activeTab = activeDragId ? tabs.find((t) => t.id === activeDragId) : null;

  const tabBar = (
    <div className={styles.scrollContainer}>
      <div className={styles.tabList} role="tablist">
        {tabs.map((tab) => (
          <DraggableTab
            key={tab.id}
            tab={tab}
            isActive={tab.id === activeTabId}
            isDraggable={draggable}
            isRemovable={removable}
            isRemoved={removedTabIds.has(tab.id)}
            onClick={() => onTabClick(tab)}
            onRemove={onRemove ? () => onRemove(tab) : undefined}
            onUndoRemove={onUndoRemove ? () => onUndoRemove(tab) : undefined}
          />
        ))}
      </div>
    </div>
  );

  if (!draggable) {
    return (
      <div className={`${styles.root}${className ? ` ${className}` : ''}`} {...rest}>
        {tabBar}
      </div>
    );
  }

  return (
    <div className={`${styles.root}${className ? ` ${className}` : ''}`} {...rest}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={tabs.map((t) => t.id)} strategy={horizontalListSortingStrategy}>
          {tabBar}
        </SortableContext>

        <DragOverlay>
          {activeTab ? (
            <div className={`${styles.tab} ${styles['tab--overlay']}`}>
              <span className={styles.tab__label}>{activeTab.label}</span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
